import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoginIcon from "@mui/icons-material/Login";
import { AssetDetailsAPI } from "../viewmodel/AssetDetailsAPI";

// seconds -> "dd/mm/yyyy, hh:mm am/pm"
const formatDateTime = (value) => {
  if (!value || isNaN(Number(value))) return "";
  return new Date(Number(value) * 1000).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// Box on the asset page that opens from the "I have received this asset" email button.
// Anyone with the link sees the status, but only the employee the asset was
// assigned to (logged in) sees and can use the button.
const AckBanner = ({ token, assetId, isLoggedIn, onConfirmed }) => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null); // { status, category, employee_name, acknowledged_at }
  const [check, setCheck] = useState(null); // { can_confirm, message } for the logged-in person
  const [needLogin, setNeedLogin] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        setCheck(null);
        setNeedLogin(!isLoggedIn);
        const data = await AssetDetailsAPI.getAckStatus(token);
        setInfo(data);

        // Still waiting and logged in: is this the right employee?
        if (data?.status === "pending" && isLoggedIn) {
          try {
            setCheck(await AssetDetailsAPI.checkAck(token));
          } catch (err) {
            if (err.response?.status === 401) setNeedLogin(true); // login expired
            else setError(err.response?.data?.message || "Something went wrong. Please try again.");
          }
        }
      } catch (err) {
        setError(err.response?.data?.message || "This confirmation link is not valid.");
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
  }, [token, isLoggedIn]);

  // Log in, then come back to this same link
  const handleLogin = () => {
    sessionStorage.setItem(
      "logingsoneurl",
      `/asset?id=${encodeURIComponent(assetId)}&ack=${encodeURIComponent(token)}`
    );
    navigate("/login");
  };

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      const data = await AssetDetailsAPI.confirmAck(token);
      setInfo(data);
      if (onConfirmed) onConfirmed();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) setNeedLogin(true);
      else if (status === 403) setCheck({ can_confirm: false, message: err.response?.data?.message });
      else if (err.response?.data?.data) setInfo(err.response.data.data); // e.g. assignment ended
      else setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  const box = (bg, border) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 1.5,
    backgroundColor: bg,
    border: `1px solid ${border}`,
    borderRadius: "8px",
    p: "14px 16px",
    mt: "0.8em",
    mb: "0.5em",
  });

  if (!token) return null;

  if (loading) {
    return (
      <Box sx={{ ...box("#f5f5f5", "#e0e0e0"), alignItems: "center" }}>
        <CircularProgress size={18} />
        <Typography sx={{ fontSize: "0.9rem" }}>Checking your confirmation link…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={box("#fff3e0", "#ffcc80")}>
        <InfoOutlinedIcon sx={{ color: "#e65100" }} />
        <Typography sx={{ fontSize: "0.9rem", color: "#6a4500" }}>{error}</Typography>
      </Box>
    );
  }

  if (info?.status === "confirmed") {
    return (
      <Box sx={box("#e8f5e9", "#a5d6a7")}>
        <CheckCircleIcon sx={{ color: "#2e7d32" }} />
        <Box>
          <Typography sx={{ fontWeight: 600, color: "#2e7d32" }}>
            Thank you, receipt confirmed
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#555" }}>
            {info.employee_name} confirmed receiving this {info.category || "asset"} on{" "}
            {formatDateTime(info.acknowledged_at)}.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (info?.status === "ended") {
    return (
      <Box sx={box("#f5f5f5", "#e0e0e0")}>
        <InfoOutlinedIcon sx={{ color: "#777" }} />
        <Typography sx={{ fontSize: "0.9rem", color: "#555" }}>
          This assignment has ended. The asset is no longer assigned to {info.employee_name}.
        </Typography>
      </Box>
    );
  }

  // pending + not logged in: ask to log in first (no confirm button)
  if (needLogin) {
    return (
      <Box sx={{ ...box("#e3f2fd", "#90caf9"), flexWrap: "wrap", alignItems: "center" }}>
        <InfoOutlinedIcon sx={{ color: "#1565c0" }} />
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontWeight: 600, color: "#0d47a1" }}>
            Please log in to confirm you have received this {info?.category || "asset"}
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#555" }}>
            Assigned to <b>{info?.employee_name}</b>. Only {info?.employee_name} can confirm,
            after logging in.
          </Typography>
        </Box>
        <Button variant="contained" onClick={handleLogin} startIcon={<LoginIcon />}>
          Log in to confirm
        </Button>
      </Box>
    );
  }

  // pending + logged in as someone else: no button
  if (check && !check.can_confirm) {
    return (
      <Box sx={box("#f5f5f5", "#e0e0e0")}>
        <InfoOutlinedIcon sx={{ color: "#777" }} />
        <Typography sx={{ fontSize: "0.9rem", color: "#555" }}>
          {check.message ||
            `Only ${info?.employee_name} can confirm receiving this asset.`}
        </Typography>
      </Box>
    );
  }

  // pending + logged in as the right employee: show the button
  if (check?.can_confirm) {
    return (
      <Box sx={{ ...box("#e3f2fd", "#90caf9"), flexWrap: "wrap", alignItems: "center" }}>
        <InfoOutlinedIcon sx={{ color: "#1565c0" }} />
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontWeight: 600, color: "#0d47a1" }}>
            Please confirm you have received this {info?.category || "asset"}
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#555" }}>
            Assigned to <b>{info?.employee_name}</b>. Click the button only when the asset is with you.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirm}
          disabled={confirming}
          startIcon={!confirming && <CheckCircleIcon />}
        >
          {confirming ? <CircularProgress size={20} color="inherit" /> : "I have received this asset"}
        </Button>
      </Box>
    );
  }

  return null;
};

export default AckBanner;